/**
 * Sync Status Service
 * Tracks last sync time and displays "Last synced X minutes ago" everywhere
 */

import { useEffect, useState } from 'react';

interface SyncStatus {
  lastSyncTime: Date | null;
  isSyncing: boolean;
  nextSyncTime: Date | null;
  pendingChanges: number;
  failedSyncs: number;
  syncError: string | null;
}

class SyncStatusService {
  private static instance: SyncStatusService;
  private status: SyncStatus = {
    lastSyncTime: null,
    isSyncing: false,
    nextSyncTime: null,
    pendingChanges: 0,
    failedSyncs: 0,
    syncError: null,
  };
  private listeners: Set<(status: SyncStatus) => void> = new Set();
  private updateInterval: NodeJS.Timeout | null = null;

  private constructor() {
    this.loadFromStorage();
    this.startUpdateInterval();
  }

  static getInstance(): SyncStatusService {
    if (!SyncStatusService.instance) {
      SyncStatusService.instance = new SyncStatusService();
    }
    return SyncStatusService.instance;
  }

  /**
   * Load last sync time from storage
   */
  private loadFromStorage() {
    const saved = localStorage.getItem('oilwellx:lastSync');
    if (saved) {
      try {
        const data = JSON.parse(saved);
        this.status.lastSyncTime = data.lastSyncTime ? new Date(data.lastSyncTime) : null;
        this.status.pendingChanges = data.pendingChanges || 0;
        this.status.failedSyncs = data.failedSyncs || 0;
      } catch (error) {
        console.error('Failed to load sync status:', error);
      }
    }
  }

  /**
   * Save sync status to storage
   */
  private saveToStorage() {
    const data = {
      lastSyncTime: this.status.lastSyncTime?.toISOString(),
      pendingChanges: this.status.pendingChanges,
      failedSyncs: this.status.failedSyncs,
    };
    localStorage.setItem('oilwellx:lastSync', JSON.stringify(data));
  }

  /**
   * Start interval to update "time ago" every minute
   */
  private startUpdateInterval() {
    // Update every 30 seconds to keep "X minutes ago" fresh
    this.updateInterval = setInterval(() => {
      this.notifyListeners();
    }, 30 * 1000);
  }

  /**
   * Mark sync as started
   */
  startSync() {
    this.status.isSyncing = true;
    this.status.syncError = null;
    this.notifyListeners();
  }

  /**
   * Mark sync as completed successfully
   */
  completeSync(syncedCount: number = 0) {
    this.status.isSyncing = false;
    this.status.lastSyncTime = new Date();
    this.status.pendingChanges = Math.max(0, this.status.pendingChanges - syncedCount);
    this.status.failedSyncs = 0;
    this.status.syncError = null;
    
    this.saveToStorage();
    this.notifyListeners();

  }

  /**
   * Mark sync as failed
   */
  failSync(error: string) {
    this.status.isSyncing = false;
    this.status.syncError = error;
    this.status.failedSyncs++;
    
    this.saveToStorage();
    this.notifyListeners();

    console.error(`❌ Sync failed:`, error);
  }

  /**
   * Add pending changes to sync queue
   */
  addPendingChanges(count: number = 1) {
    this.status.pendingChanges += count;
    this.saveToStorage();
    this.notifyListeners();
  }

  /**
   * Set next scheduled sync time
   */
  setNextSyncTime(time: Date) {
    this.status.nextSyncTime = time;
    this.notifyListeners();
  }

  /**
   * Get current sync status
   */
  getStatus(): SyncStatus {
    return { ...this.status };
  }

  /**
   * Get formatted "time ago" string
   */
  getLastSyncText(): string {
    if (!this.status.lastSyncTime) {
      return 'Never synced';
    }

    const now = new Date();
    const diff = now.getTime() - this.status.lastSyncTime.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (minutes < 1) {
      return 'Just now';
    } else if (minutes < 60) {
      return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
    } else if (hours < 24) {
      return `${hours} hour${hours === 1 ? '' : 's'} ago`;
    } else {
      return `${days} day${days === 1 ? '' : 's'} ago`;
    }
  }

  /**
   * Get sync status badge color
   */
  getStatusColor(): string {
    if (this.status.isSyncing) {
      return 'blue';
    } else if (this.status.syncError) {
      return 'red';
    } else if (this.status.pendingChanges > 0) {
      return 'yellow';
    } else {
      return 'green';
    }
  }

  /**
   * Get sync status icon
   */
  getStatusIcon(): string {
    if (this.status.isSyncing) {
      return 'refresh-cw'; // Spinning icon
    } else if (this.status.syncError) {
      return 'alert-circle';
    } else if (this.status.pendingChanges > 0) {
      return 'upload-cloud';
    } else {
      return 'check-circle';
    }
  }

  /**
   * Get sync status text
   */
  getStatusText(): string {
    if (this.status.isSyncing) {
      return 'Syncing...';
    } else if (this.status.syncError) {
      return `Sync failed: ${this.status.syncError}`;
    } else if (this.status.pendingChanges > 0) {
      return `${this.status.pendingChanges} change${this.status.pendingChanges === 1 ? '' : 's'} pending`;
    } else if (this.status.lastSyncTime) {
      return `Synced ${this.getLastSyncText()}`;
    } else {
      return 'Not synced';
    }
  }

  /**
   * Check if data is stale (not synced in > 1 hour)
   */
  isStale(): boolean {
    if (!this.status.lastSyncTime) return true;
    
    const now = new Date();
    const diff = now.getTime() - this.status.lastSyncTime.getTime();
    const hours = diff / (60 * 60 * 1000);
    
    return hours > 1;
  }

  /**
   * Check if sync is overdue (pending changes > 5 mins old)
   */
  isSyncOverdue(): boolean {
    if (this.status.pendingChanges === 0) return false;
    if (!this.status.lastSyncTime) return true;
    
    const now = new Date();
    const diff = now.getTime() - this.status.lastSyncTime.getTime();
    const minutes = diff / 60000;
    
    return minutes > 5;
  }

  /**
   * Subscribe to status changes
   */
  subscribe(callback: (status: SyncStatus) => void): () => void {
    this.listeners.add(callback);
    // Immediately call with current status
    callback(this.status);
    return () => this.listeners.delete(callback);
  }

  /**
   * Notify all listeners
   */
  private notifyListeners() {
    this.listeners.forEach(callback => callback(this.status));
  }

  /**
   * Cleanup
   */
  destroy() {
    if (this.updateInterval) {
      clearInterval(this.updateInterval);
    }
  }
}

// Export singleton instance
export const syncStatus = SyncStatusService.getInstance();

// React hook for sync status
export function useSyncStatus() {
  const [status, setStatus] = useState(syncStatus.getStatus());

  useEffect(() => {
    const unsubscribe = syncStatus.subscribe(setStatus);
    return unsubscribe;
  }, []);

  return {
    status,
    lastSyncTime: status.lastSyncTime,
    isSyncing: status.isSyncing,
    pendingChanges: status.pendingChanges,
    syncError: status.syncError,
    lastSyncText: syncStatus.getLastSyncText(),
    statusColor: syncStatus.getStatusColor(),
    statusIcon: syncStatus.getStatusIcon(),
    statusText: syncStatus.getStatusText(),
    isStale: syncStatus.isStale(),
    isSyncOverdue: syncStatus.isSyncOverdue(),
  };
}
